import styled from 'styled-components'

/**
 * Componente MobileCard per visualizzare dati tabellari in formato card su mobile
 *
 * Props:
 * @param {Array} data - Array di oggetti da visualizzare
 * @param {Array} fields - Configurazione campi [{ key, label, primary?, type?, render? }]
 * @param {Array} actions - Azioni disponibili [{ icon: Component, onClick: (item) => void, color? }]
 * @param {Function} onCardClick - Callback opzionale per click sulla card
 *
 * Esempio:
 * <MobileCardList
 *   data={clienti}
 *   fields={[
 *     { key: 'nome', label: 'Nome', primary: true },
 *     { key: 'email', label: 'Email', type: 'email' },
 *     { key: 'telefono', label: 'Telefono' },
 *   ]}
 *   actions={[
 *     { icon: FiInfo, onClick: handleInfo, color: '#3b82f6' },
 *     { icon: FiEdit2, onClick: handleEdit, color: '#3b82f6' },
 *   ]}
 * />
 */

export const MobileCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  padding: 0 0.5rem;
  padding-bottom: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.scrollbar.track};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.scrollbar.thumb};
    border-radius: 3px;
  }
`

export const MobileCard = styled.div`
  background: ${props => props.theme.colors.background.primary};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.card};
  padding: 1rem;
  box-shadow: 0 1px 3px ${props => props.theme.colors.shadow.light};
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.99);
    box-shadow: 0 1px 2px ${props => props.theme.colors.shadow.light};
  }
`

export const MobileCardHeader = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const MobileCardBadge = styled.span`
  font-size: 0.65rem;
  padding: 2px 6px;
  background: ${props => props.background || props.theme.colors.semantic.info};
  color: white;
  border-radius: 4px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 3px;
`

export const MobileCardFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const MobileCardField = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  padding: 0.25rem 0;
`

export const MobileCardFieldLabel = styled.span`
  color: ${props => props.theme.colors.text.muted};
  font-weight: 400;
`

export const MobileCardFieldValue = styled.span`
  color: ${props => props.theme.colors.text.secondary};
  font-weight: 500;
  text-align: right;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  a {
    color: ${props => props.theme.colors.semantic.info};
    text-decoration: none;
  }
`

export const MobileCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
`

export const MobileCardActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: ${props => props.theme.radius.button};
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.color || props.theme.colors.semantic.info};
  cursor: pointer;
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.95);
    background: ${props => props.theme.colors.background.hover};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

export const MobileCardEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: ${props => props.theme.colors.text.muted};
  font-style: italic;
`
